/**
 * 技能HTTP API服务
 *
 * 封装技能相关的REST API调用，用于技能CRUD操作。
 * 使用 HttpOnly Cookie 进行认证，浏览器自动携带 Token。
 */

import type {
  Skill,
  SkillDetail,
  SkillCreateRequest,
  SkillUpdateRequest,
  SkillsListResponse,
} from '../types/skill';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export class SkillHttpError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'SkillHttpError';
  }
}

export class SkillUnauthorizedError extends SkillHttpError {
  constructor(message: string = '未授权，请重新登录') {
    super(message, 401, 'UNAUTHORIZED');
    this.name = 'SkillUnauthorizedError';
  }
}

export class SkillNotFoundError extends SkillHttpError {
  constructor(message: string = '技能不存在') {
    super(message, 404, 'NOT_FOUND');
    this.name = 'SkillNotFoundError';
  }
}

export class SkillForbiddenError extends SkillHttpError {
  constructor(message: string = '没有权限执行此操作') {
    super(message, 403, 'FORBIDDEN');
    this.name = 'SkillForbiddenError';
  }
}

export class SkillValidationError extends SkillHttpError {
  constructor(message: string) {
    super(message, 400, 'VALIDATION_ERROR');
    this.name = 'SkillValidationError';
  }
}

function getHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
  };
}

async function handleResponseError(response: Response): Promise<never> {
  let errorMessage = '请求失败';

  try {
    const data = await response.json();
    errorMessage = data.detail || data.message || errorMessage;
  } catch {
    // ignore
  }

  switch (response.status) {
    case 400:
      throw new SkillValidationError(errorMessage);
    case 401:
      throw new SkillUnauthorizedError(errorMessage);
    case 403:
      throw new SkillForbiddenError(errorMessage);
    case 404:
      throw new SkillNotFoundError(errorMessage);
    default:
      throw new SkillHttpError(errorMessage, response.status);
  }
}

export class SkillApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * 获取所有可用技能列表
   */
  async listSkills(): Promise<Skill[]> {
    const response = await fetch(`${this.baseUrl}/api/skills`, {
      method: 'GET',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    const data: SkillsListResponse = await response.json();
    return data.skills;
  }

  /**
   * 获取指定技能的完整信息
   */
  async getSkill(skillName: string): Promise<SkillDetail> {
    const response = await fetch(`${this.baseUrl}/api/skills/${skillName}`, {
      method: 'GET',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 创建用户自定义技能
   */
  async createSkill(data: SkillCreateRequest): Promise<SkillDetail> {
    const response = await fetch(`${this.baseUrl}/api/skills`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 更新用户自定义技能
   */
  async updateSkill(skillId: number, data: SkillUpdateRequest): Promise<SkillDetail> {
    const response = await fetch(`${this.baseUrl}/api/skills/${skillId}`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 删除用户自定义技能
   */
  async deleteSkill(skillId: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/skills/${skillId}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }
  }
}

export const skillApi = new SkillApiService();

export default skillApi;
